import { Box, Typography, alpha, useTheme } from "@mui/material";
import { FormCard } from "components";

export interface SectorDisplay {
	id: string;
	name: string;
}

export interface PlantDisplay {
	id: string;
	name: string;
	sectorId?: string;
}

export function SectionHeader({ children }: { children: React.ReactNode }) {
	const theme = useTheme();
	return (
		<Box sx={{ mb: 1, mt: 0.5 }}>
			<Typography
				sx={{
					fontWeight: 600,
					fontSize: "0.75rem",
					color: alpha(theme.palette.text.primary, 0.5),
					textTransform: "uppercase",
					letterSpacing: "0.06em",
				}}
			>
				{children}
			</Typography>
		</Box>
	);
}

export function ReadonlyField({
	label,
	value,
	testId: testIdOverride,
}: {
	label: string;
	value: React.ReactNode;
	testId?: string;
}) {
	const testId = testIdOverride ?? `containment-field-${label.toLowerCase().replace(/\s+/g, "-")}`;
	return (
		<Box
			data-test-selector={testId}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 0.25,
				minWidth: 0,
				pl: 1.5,
			}}
		>
			<Typography
				sx={{
					fontWeight: 700,
					fontSize: "0.82rem",
					color: "text.secondary",
				}}
			>
				{label}
			</Typography>
			<Typography
				sx={{
					fontSize: "0.85rem",
					color: "text.primary",
					wordBreak: "break-word",
					whiteSpace: "pre-wrap",
					minWidth: 0,
					pl: 1.5,
				}}
			>
				{value}
			</Typography>
		</Box>
	);
}

export function ReadonlySection({
	title,
	children,
}: {
	title: React.ReactNode;
	children: React.ReactNode;
}) {
	const theme = useTheme();
	return (
		<Box
			sx={{
				borderRadius: 1.5,
				backgroundColor: alpha(theme.palette.primary.main, 0.02),
				border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
				p: 1.5,
				display: "flex",
				flexDirection: "column",
				gap: 2.0,
				width: "100%",
				flex: 1,
			}}
		>
			<SectionHeader>{title}</SectionHeader>
			{children}
		</Box>
	);
}

interface cardShellProps {
	readonly?: boolean;
	label: React.ReactNode;
	toolTip?: string;
	gap?: number;
	variant?: "default" | "nested";
	children: React.ReactNode;
}

export function CardShell({
	readonly,
	label,
	toolTip,
	gap = 2,
	variant = "default",
	children,
}: cardShellProps) {
	if (readonly) {
		return <ReadonlySection title={label}>{children}</ReadonlySection>;
	}
	return (
		<FormCard label={label as string} toolTip={toolTip} variant={variant}>
			<Box display="flex" flexDirection="column" gap={gap}>
				{children}
			</Box>
		</FormCard>
	);
}
